/**
 * EditorStore.types.ts
 * Pure type definitions, constants, and helpers extracted from EditorStore.tsx.
 * All symbols are re-exported from EditorStore.tsx so consumer import paths stay identical.
 */

import type { StudioPreparedMedia } from "../types";
import type { TimelineComment } from "../types/comments";
import type { FxBlock } from "./fxTypes";

// ──────────────────────────────────────────────
// Type exports
// ──────────────────────────────────────────────

export type Asset = {
  id: string;
  type: "video" | "audio" | "image" | "text";
  src: string;
  /** Optional lower-cost render source for editor preview/runtime only. */
  previewUrl?: string;
  /** Optional original remote URL for restoring blob-backed media */
  sourceUrl?: string;
  uploadId?: string;
  sourceUploadId?: string;
  preparedUploadId?: string;
  posterUrl?: string;
  preparedMedia?: StudioPreparedMedia;
  /**
   * When true, runtime hydration should hold on the poster/loading shell until
   * a render-safe prepared/proxy source lands.
   */
  deferVideoHydration?: boolean;
  duration: number;        // seconds (best-effort; updates post metadata)
  name?: string;
  w?: number;
  h?: number;
  /** Best-effort: whether a video has an embedded audio track. */
  hasAudio?: boolean;
};

export type Track = {
  id: string;
  kind: "video" | "audio" | "overlay" | "text" | "effects" | "transitions" | "camera";
  order: number;
  name?: string;
  locked?: boolean;   // prevent clip edits on this track
  muted?: boolean;    // mute audio for this track
  solo?: boolean;     // solo this track (mute others)
  height?: "small" | "medium" | "large"; // track row height
  /** UI-only visibility toggle (kept optional for backwards compatibility). */
  visible?: boolean;
};

export type ColorAdjustments = {
  brightness?: number;    // -100 to 100
  contrast?: number;      // -100 to 100
  saturation?: number;    // -100 to 100
  exposure?: number;      // -100 to 100
  highlights?: number;    // -100 to 100
  shadows?: number;       // -100 to 100
  temperature?: number;   // -100 (cool) to 100 (warm)
};

export type AudioAdjustments = {
  volume?: number;        // 0 to 200 (100 = normal)
  fadeIn?: number;        // seconds
  fadeOut?: number;       // seconds
  ducking?: boolean;      // auto-lower when speech present
  eq?: "voice" | "bass" | "treble" | "none"; // EQ preset
};

export type Clip = {
  id: string;
  assetId: string;
  /** canvas element id to render/show on the Stage */
  elementId?: string;
  /** Optional parent clip for nested lanes (e.g., text attached to an overlay) */
  parentClipId?: string;
  /** Optional compound clip container id (Template/Compound Clip feature) */
  templateClipId?: string;
  /**
   * "template" clips are compound clips (containers) that appear as a single clip
   * on the root timeline while their children remain editable inside (nested view).
   */
  clipType?: "template";
  trackId: string;

  /** timeline placement */
  start: number; // sec on timeline
  end: number;   // sec on timeline (exclusive)

  /** source in/out trimming within the asset */
  in: number;    // sec within the asset
  out: number;   // sec within the asset
  name?: string;
  locked?: boolean;
  muted?: boolean;
  /** Video-only: if true, the source video's embedded audio is disabled (reversible). */
  removeOriginalAudio?: boolean;

  /** Phase 3: Advanced properties */
  speed?: number;         // playback speed 0.25-4x (default 1)
  maintainPitch?: boolean; // maintain pitch at different speeds
  color?: ColorAdjustments;
  audio?: AudioAdjustments;
};

export type Marker = { id: string; t: number; label?: string };

export type Keyframe = {
  id: string;
  clipId: string;
  time: number; // relative to clip start
  property: string; // e.g., 'opacity', 'scale', 'position.x'
  value: unknown;
  easing?: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';
};

export type Transition = {
  id: string;
  fromClipId: string;
  toClipId: string;
  type: 'fade' | 'crossfade' | 'dissolve' | 'fade-black' | 'fade-white' | 'dip-black' | 'dip-white'
    | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down'
    | 'push-left' | 'push-right' | 'push-up' | 'push-down'
    | 'zoom-in' | 'zoom-out' | 'zoom-blur-in' | 'zoom-blur-out' | 'zoom-rotate-in' | 'zoom-rotate-out'
    | 'wipe-left' | 'wipe-right' | 'wipe-up' | 'wipe-down'
    | 'wipe-clock' | 'wipe-counter-clock' | 'wipe-radial' | 'wipe-iris'
    | 'wipe-diagonal-tl' | 'wipe-diagonal-tr' | 'wipe-diagonal-bl' | 'wipe-diagonal-br'
    | 'blur-through' | 'blur-directional' | 'blur-radial' | 'blur-zoom'
    | 'spin-in' | 'spin-out' | 'spin-3d-x' | 'spin-3d-y'
    | 'flip-horizontal' | 'flip-vertical' | 'flip-cube-left' | 'flip-cube-right'
    | 'split-horizontal' | 'split-vertical' | 'split-diagonal' | 'split-zoom' | 'split-blinds-h' | 'split-blinds-v'
    | 'morph-circle' | 'morph-diamond' | 'morph-heart' | 'morph-star'
    | 'glitch-rgb' | 'glitch-slice' | 'glitch-digital' | 'glitch-noise'
    | 'pixelate' | 'mosaic' | 'ripple' | 'swirl' | 'light-leak' | 'film-burn';
  duration: number; // 0.1 to 2 seconds
};

/**
 * Effect Region: defines a time range with volume/speed modifiers.
 * When playhead is inside the region, effects are applied.
 * When playhead exits, playback returns to normal.
 */
export type EffectRegion = {
  id: string;
  startMs: number;       // timeline time (milliseconds)
  endMs: number;         // timeline time (milliseconds)
  /** Region FX scoping: apply to these clips. Supports multiple simultaneous elements. */
  targetClipIds?: string[];
  /** @deprecated Use targetClipIds instead */
  targetClipId?: string;
  /** Track-based scoping: apply to all clips on these tracks. Takes precedence over clip-based scoping. */
  targetTrackIds?: string[];
  loopEnabled: boolean;
  volumeMultiplier: number; // 0..2 (1 = normal)
  playbackRate: number;     // 0.25..4 (1 = normal)
  rampMs: number;           // base ramp duration for transitions
  name?: string;       // optional label
};

// History entry for undo/redo
export type HistoryEntry = {
  clips: Clip[];
  tracks: Track[];
  keyframes: Keyframe[];
  transitions: Transition[];
  effectRegions: EffectRegion[];
  fxBlocks: FxBlock[];
};

// ──────────────────────────────────────────────
// Configuration constants
// ──────────────────────────────────────────────

export const MAX_HISTORY = 50; // Limit history to prevent memory issues
export const MAX_EFFECT_REGIONS = 50; // Safety cap for Region FX
export const FX_BLOCK_DEFAULT_CREATE_WINDOW_SEC = 2;
export const FX_BLOCK_DEFAULT_CREATE_WINDOW_EPSILON_SEC = 0.25;

// ──────────────────────────────────────────────
// FX block helper type (used by exported FX helpers in EditorStore)
// ──────────────────────────────────────────────

export type FxBlockTargetClipWindow = Pick<Clip, "id" | "start" | "end">;

// ──────────────────────────────────────────────
// Reserved track IDs
// ──────────────────────────────────────────────

export const VIDEO_TRACK_ID = "video-base";
export const CAMERA_TRACK_ID = "camera-base";
export const AUDIO_TRACK_ID = "audio-base";
export const OVERLAY_TRACK_ID = "overlay-base";
export const TEXT_TRACK_ID = "text-base";
export const EFFECTS_TRACK_ID = "effects-base";
export const TRANSITIONS_TRACK_ID = "transitions-base";

// Heuristic to detect an empty placeholder clip on the video track
export const PLACEHOLDER_ASSET_ID = "__placeholder__";
export const MISSING_ASSET_PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iMjI1Ij48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMTMxMzEzIi8+PHRleHQgeD0iNTAlIiB5PSI0OCUiIGR5PSIwLjNlbSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE2IiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5NaXNzaW5nIG1lZGlhPC90ZXh0Pjx0ZXh0IHg9IjUwJSIgeT0iNjQlIiBkeT0iMC4zZW0iIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iI2RkZCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UmUtdXBsb2FkIHRvIHJlc3RvcmU8L3RleHQ+PC9zdmc+";

// ──────────────────────────────────────────────
// Pure helper functions
// ──────────────────────────────────────────────

export const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
export const id = () => crypto.randomUUID();

export const normalizeEffectRegion = (r: any): EffectRegion | null => {
  if (!r || typeof r !== "object") return null;
  const rid = typeof r.id === "string" ? r.id : null;
  if (!rid) return null;

  // Backwards compatibility: allow {start,end} seconds + {volume,speed}.
  const startMsRaw =
    typeof r.startMs === "number"
      ? r.startMs
      : typeof r.start === "number"
        ? r.start * 1000
        : 0;
  const endMsRaw =
    typeof r.endMs === "number"
      ? r.endMs
      : typeof r.end === "number"
        ? r.end * 1000
        : startMsRaw + 100;

  const startMs = Math.max(0, Math.floor(startMsRaw));
  const endMs = Math.max(startMs + 10, Math.floor(endMsRaw));

  const legacyVolPct = typeof r.volume === "number" ? r.volume : undefined;
  const volumeMultiplierRaw =
    typeof r.volumeMultiplier === "number"
      ? r.volumeMultiplier
      : typeof legacyVolPct === "number"
        ? legacyVolPct / 100
        : 1;
  const volumeMultiplier = clamp(volumeMultiplierRaw, 0, 2);

  const legacySpeed = typeof r.speed === "number" ? r.speed : undefined;
  const playbackRateRaw =
    typeof r.playbackRate === "number"
      ? r.playbackRate
      : typeof legacySpeed === "number"
        ? legacySpeed
        : 1;
  const playbackRate = clamp(playbackRateRaw, 0.25, 4);

  const rampMsRaw = typeof r.rampMs === "number" ? r.rampMs : 0;
  const rampMs = clamp(Math.floor(rampMsRaw || 0), 0, 1000);

  // Support both targetClipIds (array) and legacy targetClipId (string)
  let targetClipIds: string[] | undefined;
  if (Array.isArray(r.targetClipIds) && r.targetClipIds.length > 0) {
    targetClipIds = r.targetClipIds.filter((id: any) => typeof id === "string" && id);
  } else if (typeof r.targetClipId === "string" && r.targetClipId) {
    targetClipIds = [r.targetClipId];
  }

  // Support track-based scoping via targetTrackIds
  let targetTrackIds: string[] | undefined;
  if (Array.isArray(r.targetTrackIds) && r.targetTrackIds.length > 0) {
    targetTrackIds = r.targetTrackIds.filter((id: any) => typeof id === "string" && id);
  }

  return {
    id: rid,
    startMs,
    endMs,
    targetClipIds: targetClipIds?.length ? targetClipIds : undefined,
    // Keep deprecated field for backward compat with existing saved data
    targetClipId: targetClipIds?.[0],
    targetTrackIds: targetTrackIds?.length ? targetTrackIds : undefined,
    loopEnabled: !!r.loopEnabled,
    volumeMultiplier,
    playbackRate,
    rampMs,
    name: typeof r.name === "string" ? r.name : undefined,
  };
};

// ──────────────────────────────────────────────
// TimelineState interface (public API contract)
// ──────────────────────────────────────────────

// Track limits: intentionally unlimited (CapCut/Adobe-style layering).
// The timeline UI is responsible for scrolling/virtualization as needed.

export type TimelineState = {
  assets: Asset[];
  /** Visible tracks. (Audio track omitted until an audio clip exists.) */
  tracks: Track[];
  clips: Clip[];
  markers: Marker[];
  keyframes: Keyframe[];
  transitions: Transition[];
  effectRegions: EffectRegion[];
  fxBlocks: FxBlock[];

  playhead: number; // seconds
  isPlaying: boolean;
  loopPlayback: boolean;
  loopRegion: { start: number; end: number } | null;

  // Timeline comments
  commentsEnabled: boolean;
  comments: TimelineComment[];

  masterMuted: boolean;
  pxPerSec: number;
  zoom: number; // 0.25..8

  selection: { clipIds: string[] };

  // selectors
  hasVideoClips: () => boolean;
  hasAudioClips: () => boolean;
  canAddTrack: (kind: Track["kind"]) => boolean;

  // asset ops
  addAsset: (a: Asset) => void;
  upsertAsset: (a: Asset) => void;

  // track ops
  addTrack: (t: Track) => void;
  removeTrack: (id: string) => void;
  updateTrack: (id: string, patch: Partial<Track>) => void;
  reorderTracks: (trackIds: string[]) => void;

  // clip ops
  addClip: (c: Clip) => void;
  updateClip: (id: string, patch: Partial<Clip>) => void;
  deleteClip: (id: string) => void;
  splitClipAt: (clipId: string, t: number) => void;
  moveClip: (id: string, newStart: number, ripple?: boolean) => void;
  trimLeft: (id: string, newStart: number, ripple?: boolean) => void;
  trimRight: (id: string, newEnd: number, ripple?: boolean) => void;
  rippleDelete: (clipId: string) => void;
  slipEdit: (clipId: string, deltaIn: number) => void;

  // playback
  setPlayhead: (t: number) => void;
  setPlaying: (p: boolean) => void;
  setLoopPlayback: (loop: boolean) => void;
  setLoopRegion: (start: number, end: number) => void;
  clearLoopRegion: () => void;

  // comment ops
  setCommentsEnabled: (enabled: boolean) => void;
  addComment: (timeMs: number, text?: string) => string;
  updateComment: (id: string, patch: Partial<TimelineComment>) => void;
  deleteComment: (id: string) => void;
  setComments: (comments: TimelineComment[]) => void;

  setMasterMuted: (muted: boolean) => void;
  setPxPerSec: (v: number) => void;
  setZoom: (z: number) => void;

  // selection
  selectClips: (ids: string[]) => void;
  selectAllClips: () => void;
  deselectAllClips: () => void;
  deleteSelectedClips: () => void;

  // keyframes
  addKeyframe: (keyframe: Keyframe) => void;
  updateKeyframe: (id: string, patch: Partial<Keyframe>) => void;
  deleteKeyframe: (id: string) => void;

  // PHASE 1: Volume keyframe helpers
  addVolumeKeyframe: (clipId: string, relativeTime: number, volume: number) => void;
  getVolumeKeyframes: (clipId: string) => Keyframe[];
  getVolumeAtTime: (clipId: string, relativeTime: number) => number | null;
  deleteVolumeKeyframes: (clipId: string) => void;

  // transitions
  addTransition: (transition: Transition) => void;
  updateTransition: (id: string, patch: Partial<Transition>) => void;
  deleteTransition: (id: string) => void;

  // effect regions
  addEffectRegion: (region: EffectRegion) => void;
  updateEffectRegion: (id: string, patch: Partial<EffectRegion>) => void;
  deleteEffectRegion: (id: string) => void;
  getEffectAtTime: (t: number) => {
    volume?: number;
    speed?: number;
    rampMs?: number;
    regionId?: string;
    targetClipId?: string;        // deprecated: first clip for backward compat
    targetClipIds?: string[];     // multi-element support
  } | null;
  // FX blocks
  addFxBlock: (fxBlock: FxBlock) => void;
  updateFxBlock: (id: string, patch: Partial<FxBlock>) => void;
  deleteFxBlock: (id: string) => void;
  getActiveFxBlocksAtTime: (t: number) => FxBlock[];
  selectedFxBlockId: string | null;
  setSelectedFxBlockId: (id: string | null) => void;

  // undo/redo
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  lastEditAt: number;

  // persistence
  hydrateFromManifest: (manifest: {
    assets?: Asset[];
    clips?: Clip[];
    tracks?: Track[];
    bindings?: any[];
    keyframes?: unknown[];
    transitions?: unknown[];
    effectRegions?: unknown[];
    fxBlocks?: unknown[];
  }) => void;

  // Reset
  clearTimeline: () => void;
};
